import React, { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  CircleUserRound,
  Cloud,
  CloudOff,
  Copy,
  CreditCard,
  FolderOpen,
  Globe2,
  LayoutDashboard,
  LogIn,
  LogOut,
  Plus,
  Save,
  ShieldCheck,
  Sparkles,
  Trash2,
  UploadCloud,
  UsersRound,
  XCircle,
} from 'lucide-react';
import EngineDemoUI from '../../demos/EngineDemoUI';
import { PublicPresentationViewer } from '../viewer/PublicPresentationViewer';
import { AccountPage, EmailVerificationNotice } from '../auth/AccountPage';
// @ts-ignore Legacy provider is JavaScript; kept only around the legacy engine adapter.
import { AuthProvider as LegacyAuthProvider } from '../../AuthProvider';
import MarketingHome from '../marketing/MarketingHome';
import PricingPage, { BillingPeriod, Plan, planSlug } from '../marketing/PricingPage';
import EditorWorkspace from '../editor/EditorWorkspace';
import { authApiClient, AuthTenantMember } from '../auth/AuthApiClient';
import { ProductAuthProvider, useProductAuth } from '../auth/ProductAuthProvider';
import { ProductBillingProvider, useProductBilling } from '../billing/ProductBillingProvider';
import {
  cloudRepository,
  isCloudDraftId,
  storageIdFromDraft,
} from '../cloud/CloudPresentationRepository';
import { presentationApiClient, PresenterApiError } from '../cloud/PresentationApiClient';
import {
  LocalDraftRepository,
  PresentationDraft,
  PresentationSessionController,
} from '../session/PresentationSession';
import { describeAvailability, PresentationAvailabilityController } from '../session/PresentationAvailability';
import { navigateProduct, ProductRoute, useProductRoute } from './routes';
import './product-shell.css';

const localRepository = new LocalDraftRepository();

function ProductLink({ to, children, className = '' }: { to: string; children: React.ReactNode; className?: string }) {
  return (
    <a
      href={to}
      className={className}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigateProduct(to);
      }}
    >
      {children}
    </a>
  );
}

function Brand() {
  return (
    <ProductLink to="/" className="ps-brand" aria-label="3D Presenter Startseite">
      <span className="ps-brand-mark">3D</span>
      <span>Presenter</span>
    </ProductLink>
  );
}

function ProductFrame({ children, active }: { children: React.ReactNode; active: 'dashboard' | 'profile' | 'teams' | 'editor' }) {
  const auth = useProductAuth();
  return (
    <div className={`ps-shell${active === 'editor' ? ' ps-shell-editor' : ''}`}>
      <header className="ps-topbar">
        <Brand />
        <nav className="ps-nav" aria-label="Produktnavigation">
          <ProductLink to="/app" className={active === 'dashboard' ? 'is-active' : ''}>
            <LayoutDashboard size={17} /> Präsentationen
          </ProductLink>
          <ProductLink to="/teams" className={active === 'teams' ? 'is-active' : ''}>
            <UsersRound size={17} /> Teams
          </ProductLink>
          <ProductLink to="/profile" className={active === 'profile' ? 'is-active' : ''}>
            <CircleUserRound size={17} /> Profil
          </ProductLink>
        </nav>
        <div className="ps-account">
          {auth.phase === 'authenticated' ? (
            <button className="ps-button ps-button-quiet" onClick={auth.logout}>
              <LogOut size={16} /> Abmelden
            </button>
          ) : (
            <button className="ps-button ps-button-quiet" onClick={() => void auth.login(window.location.pathname)}>
              <LogIn size={16} /> Anmelden
            </button>
          )}
        </div>
      </header>
      <EmailVerificationNotice />
      {children}
    </div>
  );
}

function AuthGate({ title, children }: { title: string; children: React.ReactNode }) {
  const auth = useProductAuth();
  if (auth.phase === 'authenticated') return <>{children}</>;
  return (
    <div className="ps-empty ps-auth-gate">
      <div className="ps-eyebrow">Konto</div>
      <h2>{title}</h2>
      <p>Die Anmeldung läuft über den zentralen Arkturian-Account. Deine Präsentation bleibt bis dahin lokal in diesem Browser erhalten.</p>
      {auth.error && <div className="ps-alert">{auth.error}</div>}
      <button className="ps-button ps-button-primary" onClick={() => void auth.login(window.location.pathname)}>
        <LogIn size={18} /> Anmelden oder Konto erstellen
      </button>
    </div>
  );
}

function DashboardPage() {
  const auth = useProductAuth();
  const billing = useProductBilling();
  const [drafts, setDrafts] = useState<PresentationDraft[]>([]);
  const [cloudDrafts, setCloudDrafts] = useState<PresentationDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudError, setCloudError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setDrafts(await localRepository.list());
    if (auth.phase === 'authenticated' && auth.user?.email_verified === true) {
      setCloudLoading(true);
      setCloudError(null);
      try {
        setCloudDrafts(await cloudRepository.list());
      } catch (reason) {
        setCloudError(reason instanceof Error ? reason.message : 'Cloud-Präsentationen konnten nicht geladen werden.');
      } finally {
        setCloudLoading(false);
      }
    } else {
      setCloudDrafts([]);
    }
    setLoading(false);
  }, [auth.phase, auth.user?.email_verified]);

  useEffect(() => void refresh(), [refresh]);

  const create = async () => {
    const draft = await localRepository.create();
    navigateProduct(`/edit/${encodeURIComponent(draft.id)}`);
  };

  const removeCloud = async (event: React.MouseEvent, draft: PresentationDraft) => {
    event.preventDefault();
    event.stopPropagation();
    if (!window.confirm(`„${draft.title}“ wirklich aus der Cloud löschen?`)) return;
    try {
      await presentationApiClient.remove(storageIdFromDraft(draft.id));
      await refresh();
    } catch (reason) {
      setCloudError(reason instanceof Error ? reason.message : 'Löschen fehlgeschlagen.');
    }
  };

  return (
    <ProductFrame active="dashboard">
      <main className="ps-page">
        <div className="ps-page-heading">
          <div>
            <div className="ps-eyebrow">Dein Raum</div>
            <h1>Präsentationen</h1>
            <p>Entwürfe beginnen lokal. Cloud, Teams und Teilen werden erst nach Anmeldung freigeschaltet.</p>
          </div>
          <button className="ps-button ps-button-primary" onClick={create}>
            <Plus size={18} /> Neue Präsentation
          </button>
        </div>

        <section className="ps-plan-strip">
          <div>
            <CreditCard size={19} />
            <span>Aktueller Tarif</span>
            <strong>{billing.status?.entitlement.tier || 'Frei'}</strong>
          </div>
          <ProductLink to={billing.status?.entitlement.tier && billing.status.entitlement.tier !== 'free' ? '/billing' : '/pricing'}>
            {billing.status?.entitlement.tier && billing.status.entitlement.tier !== 'free' ? 'Abrechnung verwalten' : 'Tarife ansehen'}
          </ProductLink>
        </section>

        <section className="ps-onboarding-card">
          <div>
            <Sparkles size={24} />
            <h2>Eine Idee reicht für den Anfang.</h2>
            <p>Starte mit drei räumlichen Stationen und ersetze sie im Editor durch deine Geschichte.</p>
          </div>
          <button className="ps-button ps-button-light" onClick={create}>Leeren Entwurf öffnen</button>
        </section>

        <section>
          <div className="ps-section-title">
            <h2>Lokale Entwürfe</h2>
            <span>{drafts.length}</span>
          </div>
          {loading ? (
            <div className="ps-empty">Entwürfe werden geladen …</div>
          ) : drafts.length === 0 ? (
            <div className="ps-empty">
              <FolderOpen size={30} />
              <h3>Noch keine Präsentation</h3>
              <p>Dein erster Entwurf wird ohne Anmeldung sicher in diesem Browser gespeichert.</p>
            </div>
          ) : (
            <div className="ps-card-grid">
              {drafts.map((draft) => (
                <ProductLink key={draft.id} to={`/edit/${encodeURIComponent(draft.id)}`} className="ps-presentation-card">
                  <div className="ps-card-preview"><span>{draft.title.slice(0, 2).toUpperCase()}</span></div>
                  <div className="ps-card-body">
                    <div className="ps-card-meta"><span>Lokal</span><span>{new Date(draft.updatedAt).toLocaleDateString('de-AT')}</span></div>
                    <h3>{draft.title}</h3>
                    <p>{draft.subtitle}</p>
                  </div>
                  <ChevronRight size={19} />
                </ProductLink>
              ))}
            </div>
          )}
        </section>

        <section className="ps-cloud-section">
          <div className="ps-section-title">
            <h2>Cloud-Präsentationen</h2>
            <span>{cloudDrafts.length}</span>
          </div>
          {auth.phase !== 'authenticated' ? (
            <div className="ps-empty ps-auth-gate">
              <Cloud size={30} />
              <h3>Auf allen Geräten weiterarbeiten</h3>
              <p>Melde dich an, um Entwürfe sicher in deiner persönlichen Cloud zu speichern und zu veröffentlichen.</p>
              <button className="ps-button ps-button-primary" onClick={() => void auth.login('/app')}>
                <LogIn size={17} /> Cloud öffnen
              </button>
            </div>
          ) : cloudLoading ? (
            <div className="ps-empty">Cloud-Bibliothek wird geladen …</div>
          ) : cloudError ? (
            <div className="ps-alert">{cloudError}</div>
          ) : cloudDrafts.length === 0 ? (
            <div className="ps-empty">
              <UploadCloud size={30} />
              <h3>Noch nichts in der Cloud</h3>
              <p>Öffne einen lokalen Entwurf und wähle „In Cloud speichern“.</p>
            </div>
          ) : (
            <div className="ps-card-grid">
              {cloudDrafts.map((draft) => (
                <div key={draft.id} className="ps-cloud-card-wrap">
                  <ProductLink to={`/edit/${encodeURIComponent(draft.id)}`} className="ps-presentation-card">
                    <div className="ps-card-preview"><span>{draft.title.slice(0, 2).toUpperCase()}</span></div>
                    <div className="ps-card-body">
                      <div className="ps-card-meta">
                        <span>{draft.publication === 'published' ? 'Veröffentlicht' : 'Privat'}</span>
                        <span>{new Date(draft.updatedAt).toLocaleDateString('de-AT')}</span>
                      </div>
                      <h3>{draft.title}</h3>
                      <p>{draft.subtitle}</p>
                    </div>
                    <ChevronRight size={19} />
                  </ProductLink>
                  <button className="ps-card-delete" aria-label={`${draft.title} löschen`} onClick={(event) => void removeCloud(event, draft)}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </ProductFrame>
  );
}

function EditorPage({ presentationId, isNew }: { presentationId: string | null; isNew: boolean }) {
  const auth = useProductAuth();
  const cloudDraft = isCloudDraftId(presentationId);
  const controller = useMemo(
    () => new PresentationSessionController(cloudDraft ? cloudRepository : localRepository),
    [cloudDraft],
  );
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  // #1762: owner hint whether the share link works for visitors. Checked on open, save,
  // publication change and manual refresh only; never polled.
  const availability = useMemo(
    () => new PresentationAvailabilityController((id) => cloudRepository.availability(id)),
    [],
  );
  const availabilityState = useSyncExternalStore(availability.subscribe, availability.getSnapshot, availability.getSnapshot);
  const availabilityMessage = describeAvailability(availabilityState);
  const checkAvailability = (trigger: 'open' | 'save' | 'publication' | 'manual') => {
    const active = controller.getSnapshot().active;
    if (!active || active.source !== 'cloud') return;
    if (active.publication !== 'published') { availability.reset(); return; }
    void availability.check(active.id, trigger);
  };
  const [saveRequest, setSaveRequest] = useState(0);
  const [cloudBusy, setCloudBusy] = useState(false);
  const [cloudError, setCloudError] = useState<string | null>(null);
  const [reauthNeeded, setReauthNeeded] = useState(false);

  // One place for save/publish failures (#1758): an expired session (401) gets a
  // re-login path and the reassurance that the edits are kept on this device.
  const reportError = (reason: unknown, fallback: string) => {
    if (reason instanceof PresenterApiError && reason.status === 401) {
      setReauthNeeded(true);
      // Only claim what the controller has actually written (#1758, Codex edge 2).
      const secured = controller.getSnapshot().recovery !== 'unavailable';
      setCloudError(secured
        ? 'Deine Anmeldung ist abgelaufen. Deine Änderungen sind auf diesem Gerät gesichert; melde dich erneut an und speichere dann.'
        : 'Deine Anmeldung ist abgelaufen und dieser Browser konnte die Änderungen NICHT sichern (Speicher blockiert). Verlasse die Seite nicht: öffne „Markdown", kopiere den Text, und melde dich dann erneut an.');
      return;
    }
    setReauthNeeded(false);
    setCloudError(reason instanceof Error ? reason.message : fallback);
  };

  useEffect(() => {
    let active = true;
    const open = async () => {
      // `/edit/new?draft=media|prompt` from the landing page: the redirect below drops the
      // query, so the intent is handed to the overlay through sessionStorage.
      try {
        const intent = new URLSearchParams(window.location.search).get('draft');
        if (intent === 'media' || intent === 'prompt') sessionStorage.setItem('3dp.draft-intent', `${intent}:${Date.now()}`);
      } catch { /* storage unavailable: no deep link, nothing lost */ }
      const draft = await controller.open(isNew ? null : presentationId);
      if (active && isNew) navigateProduct(`/edit/${encodeURIComponent(draft.id)}`, { replace: true });
      if (active && draft.source === 'cloud' && draft.publication === 'published') void availability.check(draft.id, 'open');
    };
    void open().catch(() => {});
    return () => { active = false; availability.reset(); };
  }, [availability, controller, isNew, presentationId]);

  const saveToCloud = async () => {
    if (auth.phase !== 'authenticated') {
      // Gast: erst den lokalen Entwurf sichern, dann zur Anmeldung. Scheitert das lokale
      // Sichern, startet keine Anmeldung, sonst gingen die Aenderungen seit dem letzten
      // Speichern auf dem Weg ueber auth-api verloren (Befund Post #4963).
      if (snapshot.active?.source === 'local') {
        setCloudError(null);
        try {
          await controller.save();
        } catch (reason) {
          reportError(reason, 'Lokales Speichern fehlgeschlagen.');
          return;
        }
      }
      await auth.login(window.location.pathname);
      return;
    }
    if (auth.user?.email_verified !== true) {
      setCloudError('Bitte bestätige zuerst deine E-Mail-Adresse.');
      return;
    }
    if (!snapshot.active || snapshot.active.source !== 'local') return;
    setCloudBusy(true);
    setCloudError(null);
    try {
      const savedLocal = await controller.save();
      const savedCloud = await cloudRepository.saveLocalToCloud(savedLocal);
      navigateProduct(`/edit/${encodeURIComponent(savedCloud.id)}`, { replace: true });
    } catch (reason) {
      reportError(reason, 'Cloud-Speichern fehlgeschlagen.');
    } finally {
      setCloudBusy(false);
    }
  };

  const togglePublication = async () => {
    if (!snapshot.active || snapshot.active.source !== 'cloud') return;
    setCloudBusy(true);
    setCloudError(null);
    try {
      const saved = await controller.save();
      const updated = await cloudRepository.setPublication(saved, saved.publication !== 'published');
      controller.acceptSaved(updated);
      checkAvailability('publication');
    } catch (reason) {
      reportError(reason, 'Veröffentlichung fehlgeschlagen.');
    } finally {
      setCloudBusy(false);
    }
  };

  const copyShareLink = async () => {
    if (!snapshot.active || snapshot.active.publication !== 'published') return;
    const id = storageIdFromDraft(snapshot.active.id);
    await navigator.clipboard.writeText(`${window.location.origin}/p/${id}`);
  };

  return (
    <ProductFrame active="editor">
      <main className="ps-editor">
        <div className="ps-editorhead">
        <div className="ps-editorbar">
          <ProductLink to="/app" className="ps-icon-link" aria-label="Zur Bibliothek"><ArrowLeft size={20} /></ProductLink>
          <input
            className="ps-title-input"
            value={snapshot.active?.title ?? ''}
            onChange={(event) => controller.update({ title: event.target.value })}
            aria-label="Präsentationstitel"
          />
          <div className="ps-save-state">
            {snapshot.active?.source === 'cloud' ? <Cloud size={15} /> : <CloudOff size={15} />}
            {snapshot.phase === 'saving'
              ? 'Speichert …'
              : snapshot.active?.source === 'cloud'
                ? snapshot.active.publication === 'published' ? 'Cloud · veröffentlicht' : 'Cloud · privat'
                : snapshot.phase === 'saved' ? 'Lokal gespeichert' : 'Nur auf diesem Gerät'}
          </div>
          {snapshot.active?.source === 'local' && (
            <button className="ps-button ps-button-quiet ps-cloud-action" disabled={cloudBusy} onClick={() => void saveToCloud()}>
              <UploadCloud size={17} /> {cloudBusy ? 'Lädt …' : 'In Cloud speichern'}
            </button>
          )}
          {snapshot.active?.source === 'cloud' && (
            <>
              <button className="ps-button ps-button-quiet ps-cloud-action" disabled={cloudBusy} onClick={() => void togglePublication()}>
                <Globe2 size={17} /> {snapshot.active.publication === 'published' ? 'Privat machen' : 'Veröffentlichen'}
              </button>
              {snapshot.active.publication === 'published' && (
                <button className="ps-button ps-button-quiet ps-icon-action" aria-label="Share-Link kopieren" onClick={() => void copyShareLink()}>
                  <Copy size={17} />
                </button>
              )}
            </>
          )}
          <button data-testid="save-presentation" className="ps-button ps-button-primary" disabled={!snapshot.active || snapshot.phase === 'saving'} onClick={() => setSaveRequest((value) => value + 1)}>
            <Save size={17} /> Speichern
          </button>
        </div>
        {snapshot.active?.source === 'cloud' && snapshot.active.publication === 'published' && availabilityMessage && (
          <div className={`ps-availability ps-availability-${availabilityMessage.tone}`} data-testid="share-availability" data-availability={availabilityMessage.state} role="status">
            <span>{availabilityMessage.text}</span>
            <button type="button" className="ps-availability-refresh" data-testid="share-availability-refresh"
              disabled={availabilityState.kind === 'checking'} onClick={() => checkAvailability('manual')}>
              Erneut prüfen
            </button>
          </div>
        )}
        </div>
        <div className="ps-engine-stage">
          {snapshot.phase === 'loading' || snapshot.phase === 'idle' ? (
            <div className="ps-stage-loading">3D-Raum wird vorbereitet …</div>
          ) : snapshot.active ? (
            <EditorWorkspace
              key={snapshot.active.id}
              draft={snapshot.active}
              sessionPhase={snapshot.phase}
              saveRequest={saveRequest}
              onContentChange={(content) => controller.update({ content })}
              onSave={async () => {
                setCloudError(null);
                try {
                  await controller.save();
                  checkAvailability('save');
                } catch (reason) {
                  reportError(reason, 'Speichern fehlgeschlagen.');
                  throw reason;
                }
              }}
              onUploadMedia={auth.phase === 'authenticated'
                ? async (file) => (await presentationApiClient.uploadMedia(file)).uri
                : undefined}
              onCreateDraft={(request) => presentationApiClient.createDraft(request)}
              draftAccess={auth.phase !== 'authenticated'
                ? { phase: 'guest', login: () => { void saveToCloud(); } }
                : auth.user?.email_verified === true ? { phase: 'ready' } : { phase: 'unverified' }}
            />
          ) : (
            <div className="ps-stage-loading ps-alert">{snapshot.error || 'Präsentation konnte nicht geöffnet werden.'}</div>
          )}
        </div>
        {snapshot.notice && !cloudError && <div className="ps-editor-error" style={{ background: '#fff8e8', borderColor: 'rgba(120,80,0,.25)', color: '#5a3d00' }}>{snapshot.notice}</div>}
        {cloudError && (
          <div className="ps-editor-error">
            {cloudError}
            {reauthNeeded && (
              <button className="ps-button ps-button-quiet" style={{ marginLeft: 10 }} onClick={() => auth.expireSession(window.location.pathname)}>Erneut anmelden</button>
            )}
          </div>
        )}
      </main>
    </ProductFrame>
  );
}

function ProfilePage() {
  const auth = useProductAuth();
  const billing = useProductBilling();
  const displayName = auth.user?.name || [auth.user?.first_name, auth.user?.last_name].filter(Boolean).join(' ') || '3D Presenter';
  return (
    <ProductFrame active="profile">
      <main className="ps-page ps-page-narrow">
        <div className="ps-page-heading"><div><div className="ps-eyebrow">Account</div><h1>Profil</h1></div></div>
        <AuthGate title="Melde dich an, um dein Profil zu verwalten.">
          <section className="ps-profile-card">
            <div className="ps-avatar">{displayName.slice(0, 1).toUpperCase()}</div>
            <div><h2>{displayName}</h2><p>{auth.user?.email}</p></div>
          </section>
          <section className="ps-settings-card">
            <h2>Identität</h2>
            <div className="ps-field-row"><span>Kanonische User-ID</span><code>{auth.user?.id}</code></div>
            <div className="ps-field-row"><span>Anmeldung</span><strong>AuthAPI · Federation JWT</strong></div>
          </section>
          <section className="ps-settings-card">
            <h2>Tarif und Abrechnung</h2>
            <div className="ps-field-row"><span>Aktiver Tarif</span><strong>{billing.status?.entitlement.tier || 'free'}</strong></div>
            <ProductLink to="/billing" className="ps-button ps-button-primary"><CreditCard size={17} /> Abrechnung öffnen</ProductLink>
          </section>
        </AuthGate>
      </main>
    </ProductFrame>
  );
}

function PricingRoutePage() {
  const auth = useProductAuth();
  const billing = useProductBilling();

  const selectPlan = (plan: Plan, period: BillingPeriod) => {
    if (plan.id === 'free') {
      navigateProduct('/edit/new');
      return;
    }
    const returnTo = `/onboarding?plan=${plan.id}&period=${period}`;
    if (auth.phase !== 'authenticated') {
      void auth.login(returnTo);
      return;
    }
    navigateProduct(returnTo);
  };

  return (
    <PricingPage
      billingMode={billing.config?.billing_mode || null}
      livePlans={billing.plans}
      activeTier={billing.status?.entitlement.tier || null}
      busy={billing.phase === 'loading'}
      error={null}
      onSelectPlan={selectPlan}
    />
  );
}

function selectedPlanFromUrl(): { plan: 'pro' | 'team'; period: BillingPeriod; slug: string } {
  const params = new URLSearchParams(window.location.search);
  const plan = params.get('plan') === 'team' || params.get('plan')?.startsWith('presenter-team') ? 'team' : 'pro';
  const period = params.get('period') === 'yearly' || params.get('plan')?.endsWith('-yearly') ? 'yearly' : 'monthly';
  return { plan, period, slug: planSlug(plan, period) };
}

function OnboardingPage() {
  const auth = useProductAuth();
  const billing = useProductBilling();
  const selection = selectedPlanFromUrl();
  const plan = billing.plans.find((item) => item.slug === selection.slug);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      await billing.startCheckout(selection.slug);
    } catch {
      setBusy(false);
    }
  };

  return (
    <ProductFrame active="dashboard">
      <main className="ps-page ps-page-narrow">
        <div className="ps-page-heading">
          <div><div className="ps-eyebrow">Onboarding</div><h1>Fast geschafft.</h1><p>Konto, Tarif und Zahlung bleiben drei klar getrennte Schritte.</p></div>
        </div>
        <div className="ps-onboarding-steps" aria-label="Onboarding-Schritte">
          <span className={auth.phase === 'authenticated' ? 'is-done' : 'is-current'}>1 · Konto</span>
          <span className={auth.phase === 'authenticated' ? 'is-current' : ''}>2 · Tarif</span>
          <span>3 · Zahlung</span>
        </div>
        <AuthGate title="Melde dich an, um deinen Tarif sicher zu buchen.">
          <section className="ps-checkout-card">
            <div className="ps-checkout-icon"><ShieldCheck size={28} /></div>
            <div>
              <div className="ps-eyebrow">Ausgewählt</div>
              <h2>{selection.plan === 'team' ? 'Team' : 'Pro'} · {selection.period === 'yearly' ? 'Jährlich' : 'Monatlich'}</h2>
              <p>{plan ? `${(plan.price_cents / 100).toLocaleString('de-AT')} ${plan.currency}` : 'Preis wird sicher aus Stripe geladen.'}</p>
            </div>
            {billing.config?.billing_mode === 'stripe_test' && (
              <div className="ps-test-badge">Testmodus · keine echte Abbuchung</div>
            )}
            {billing.phase === 'unavailable' && (
              <div className="ps-notice">Die sichere Zahlung wird gerade vorbereitet. Es kann noch keine Buchung ausgelöst werden.</div>
            )}
            {billing.phase === 'error' && billing.error && <div className="ps-alert">{billing.error}</div>}
            <button
              className="ps-button ps-button-primary ps-button-wide"
              disabled={busy || billing.phase !== 'ready' || !plan}
              onClick={() => void start()}
            >
              <CreditCard size={18} /> {busy ? 'Stripe wird geöffnet …' : 'Sicher mit Stripe fortfahren'}
            </button>
            <ProductLink to="/pricing" className="ps-text-link">Tarif ändern</ProductLink>
          </section>
        </AuthGate>
      </main>
    </ProductFrame>
  );
}

function CheckoutResultPage({ result }: { result: 'success' | 'cancel' }) {
  const auth = useProductAuth();
  const billing = useProductBilling();
  const selection = selectedPlanFromUrl();
  const [verification, setVerification] = useState<'pending' | 'active' | 'delayed'>(result === 'success' ? 'pending' : 'delayed');

  useEffect(() => {
    if (result !== 'success' || auth.phase !== 'authenticated') return;
    let cancelled = false;
    let attempt = 0;
    let timer: number | undefined;
    const poll = async () => {
      const status = await billing.refresh();
      if (cancelled) return;
      if (status?.entitlement.tier === selection.plan) {
        setVerification('active');
        return;
      }
      attempt += 1;
      if (attempt >= 30) {
        setVerification('delayed');
        return;
      }
      timer = window.setTimeout(() => void poll(), 2000);
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [auth.phase, billing.refresh, result, selection.plan]);

  const cancelled = result === 'cancel';
  const active = verification === 'active';
  return (
    <ProductFrame active="profile">
      <main className="ps-page ps-page-narrow">
        <section className="ps-result-card">
          {cancelled ? <XCircle size={46} /> : active ? <CheckCircle2 size={46} /> : <div className="ps-spinner" />}
          <div className="ps-eyebrow">{cancelled ? 'Abgebrochen' : active ? 'Aktiviert' : 'Zahlung wird geprüft'}</div>
          <h1>{cancelled ? 'Es wurde nichts gebucht.' : active ? 'Dein Tarif ist aktiv.' : 'Stripe bestätigt deine Zahlung.'}</h1>
          <p>
            {cancelled && 'Du kannst jederzeit zur Tarifauswahl zurückkehren.'}
            {!cancelled && verification === 'pending' && 'Wir warten auf den signierten Stripe-Webhook. Der Rücksprung allein gilt nicht als Zahlungsbeleg.'}
            {!cancelled && verification === 'delayed' && 'Die Bestätigung dauert länger als üblich. Es geht nichts verloren; dein Status wird im Konto weiter aktualisiert.'}
            {active && 'Die neuen Funktionen sind jetzt in deinem Dashboard freigeschaltet.'}
          </p>
          {billing.error && <div className="ps-alert">{billing.error}</div>}
          <div className="ps-result-actions">
            <ProductLink to={cancelled ? '/pricing' : '/app'} className="ps-button ps-button-primary">
              {cancelled ? 'Zurück zu den Tarifen' : 'Zum Dashboard'}
            </ProductLink>
            {!cancelled && !active && <ProductLink to="/billing" className="ps-button ps-button-quiet">Status im Konto ansehen</ProductLink>}
          </div>
        </section>
      </main>
    </ProductFrame>
  );
}

function BillingPage() {
  const billing = useProductBilling();
  return (
    <ProductFrame active="profile">
      <main className="ps-page ps-page-narrow">
        <div className="ps-page-heading"><div><div className="ps-eyebrow">Account</div><h1>Abrechnung</h1><p>Stripe verwaltet Zahlungsmittel, Rechnungen und Kündigung sicher außerhalb des Presenters.</p></div></div>
        <AuthGate title="Melde dich an, um deine Abrechnung zu verwalten.">
          <section className="ps-settings-card">
            <h2>Aktueller Tarif</h2>
            <div className="ps-field-row"><span>Tier</span><strong>{billing.status?.entitlement.tier || 'free'}</strong></div>
            <div className="ps-field-row"><span>Status</span><strong>{billing.status?.subscription?.status || 'Kein aktives Abo'}</strong></div>
            {billing.status?.subscription?.current_period_end && <div className="ps-field-row"><span>Nächste Periode</span><strong>{new Date(billing.status.subscription.current_period_end).toLocaleDateString('de-AT')}</strong></div>}
            {billing.error && <div className="ps-alert">{billing.error}</div>}
            {billing.status?.subscription ? (
              <button className="ps-button ps-button-primary" onClick={() => void billing.openPortal()}><CreditCard size={17} /> Stripe-Kundenportal</button>
            ) : (
              <ProductLink to="/pricing" className="ps-button ps-button-primary">Tarif wählen</ProductLink>
            )}
          </section>
        </AuthGate>
      </main>
    </ProductFrame>
  );
}

function TeamDetail({ teamId }: { teamId: string }) {
  const auth = useProductAuth();
  const [members, setMembers] = useState<AuthTenantMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<'admin' | 'editor' | 'viewer'>('viewer');
  const [busy, setBusy] = useState(false);
  const team = auth.tenants.find((item) => item.id === teamId);

  const loadMembers = useCallback(async () => {
    setMembers(await authApiClient.listTenantMembers(teamId));
  }, [teamId]);

  useEffect(() => {
    void loadMembers().catch((reason) => {
      setError(reason instanceof Error ? reason.message : 'Mitglieder konnten nicht geladen werden.');
    });
  }, [loadMembers]);

  const canManage = team?.my_role === 'owner' || team?.my_role === 'admin';
  const addMember = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authApiClient.addTeamMember(teamId, { email: memberEmail, role: memberRole });
      setMemberEmail('');
      await loadMembers();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Mitglied konnte nicht hinzugefügt werden.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="ps-page-heading"><div><div className="ps-eyebrow">Team</div><h1>{team?.display_name || teamId}</h1><p>{team?.description}</p></div></div>
      {error && <div className="ps-alert">{error}</div>}
      <section className="ps-settings-card">
        <div className="ps-section-title"><h2>Mitglieder</h2><span>{members.length}</span></div>
        {canManage && (
          <form className="ps-inline-form" onSubmit={addMember}>
            <input type="email" required value={memberEmail} onChange={(event) => setMemberEmail(event.target.value)} placeholder="person@firma.at" aria-label="E-Mail des neuen Mitglieds" />
            <select value={memberRole} onChange={(event) => setMemberRole(event.target.value as typeof memberRole)} aria-label="Rolle">
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
            <button className="ps-button ps-button-primary" disabled={busy}>{busy ? 'Fügt hinzu …' : 'Hinzufügen'}</button>
          </form>
        )}
        <div className="ps-member-list">
          {members.filter((member) => !member.system || member.system === 'presenter').map((member) => (
            <div className="ps-member" key={`${member.user_id}-${member.role}`}>
              <div className="ps-avatar ps-avatar-small">{member.name.slice(0, 1).toUpperCase()}</div>
              <div><strong>{member.name}</strong><span>{member.email}</span></div>
              <span className="ps-role">{member.role}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function TeamsPage({ teamId }: { teamId: string | null }) {
  const auth = useProductAuth();
  const billing = useProductBilling();
  const [createOpen, setCreateOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canCreateTeam = billing.status?.entitlement.tier === 'team';

  const createTeam = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canCreateTeam) {
      setError('Ein Team-Workspace ist im Team-Tarif enthalten.');
      return;
    }
    setBusy(true);
    setError(null);
    const slug = teamName
      .trim()
      .toLocaleLowerCase('de-AT')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 52);
    try {
      const team = await authApiClient.createTeam({
        id: `${slug || 'team'}-${Math.random().toString(36).slice(2, 7)}`,
        display_name: teamName,
        description: teamDescription || undefined,
      });
      await auth.refresh();
      navigateProduct(`/teams/${encodeURIComponent(team.id)}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Team konnte nicht angelegt werden.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <ProductFrame active="teams">
      <main className="ps-page">
        <AuthGate title="Melde dich an, um Teams anzulegen und zu verwalten.">
          {teamId ? <TeamDetail teamId={teamId} /> : (
            <>
              <div className="ps-page-heading">
                <div><div className="ps-eyebrow">Zusammenarbeit</div><h1>Teams</h1><p>Ein Team ist ein AuthAPI-Tenant mit klaren Owner-, Admin-, Editor- und Viewer-Rollen.</p></div>
                {canCreateTeam ? (
                  <button className="ps-button ps-button-primary" onClick={() => setCreateOpen((value) => !value)}>
                    <Plus size={18} /> Team anlegen
                  </button>
                ) : (
                  <ProductLink to="/pricing" className="ps-button ps-button-primary">
                    Team-Tarif ansehen
                  </ProductLink>
                )}
              </div>
              {error && <div className="ps-alert">{error}</div>}
              {!canCreateTeam && billing.phase !== 'loading' && (
                <div className="ps-notice">Bestehende Einladungen bleiben sichtbar. Einen eigenen Workspace kannst du nach Aktivierung des Team-Tarifs anlegen.</div>
              )}
              {createOpen && (
                <form className="ps-create-team" onSubmit={createTeam}>
                  <div><div className="ps-eyebrow">Neues Team</div><h2>Gemeinsamen Workspace anlegen</h2></div>
                  <input required minLength={2} maxLength={200} value={teamName} onChange={(event) => setTeamName(event.target.value)} placeholder="Teamname" />
                  <input maxLength={500} value={teamDescription} onChange={(event) => setTeamDescription(event.target.value)} placeholder="Kurze Beschreibung (optional)" />
                  <button className="ps-button ps-button-primary" disabled={busy}>{busy ? 'Wird angelegt …' : 'Team erstellen'}</button>
                </form>
              )}
              <div className="ps-card-grid">
                {auth.tenants.map((team) => (
                  <ProductLink key={team.id} to={`/teams/${encodeURIComponent(team.id)}`} className="ps-team-card">
                    <div className="ps-avatar">{team.display_name.slice(0, 1).toUpperCase()}</div>
                    <div><h3>{team.display_name}</h3><p>{team.description || team.id}</p></div>
                    <ChevronRight size={19} />
                  </ProductLink>
                ))}
              </div>
            </>
          )}
        </AuthGate>
      </main>
    </ProductFrame>
  );
}

function AuthCallbackPage() {
  const { refresh } = useProductAuth();
  const [status, setStatus] = useState('Anmeldung wird abgeschlossen …');
  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    const error = url.searchParams.get('error');
    if (error || !code || !state) {
      setStatus(error ? `Anmeldung abgebrochen: ${error}` : 'Ungültige Antwort des Anmeldedienstes.');
      return;
    }
    void authApiClient.completeLogin(code, state)
      .then(async (returnTo) => {
        await refresh();
        navigateProduct(returnTo, { replace: true });
      })
      .catch((reason) => setStatus(reason instanceof Error ? reason.message : 'Anmeldung fehlgeschlagen.'));
  }, [refresh]);
  return <div className="ps-callback"><Brand /><div className="ps-spinner" /><p>{status}</p></div>;
}

function LegacyPresenter() {
  const exportMode = new URLSearchParams(window.location.search).get('mode') === 'export';
  return <LegacyAuthProvider><EngineDemoUI exportMode={exportMode} /></LegacyAuthProvider>;
}

function RouteOutlet({ route }: { route: ProductRoute }) {
  switch (route.kind) {
    case 'marketing':
      return <MarketingHome onOpenApp={() => navigateProduct('/app')} onCreate={() => navigateProduct('/edit/new')} />;
    case 'dashboard': return <DashboardPage />;
    case 'editor': return <EditorPage presentationId={route.presentationId} isNew={route.isNew} />;
    case 'viewer': return <PublicPresentationViewer presentationId={route.presentationId} />;
    case 'profile': return <ProfilePage />;
    case 'teams': return <TeamsPage teamId={route.teamId} />;
    case 'pricing': return <PricingRoutePage />;
    case 'onboarding': return <OnboardingPage />;
    case 'checkoutResult': return <CheckoutResultPage result={route.result} />;
    case 'billing': return <BillingPage />;
    case 'authCallback': return <AuthCallbackPage />;
    case 'auth': return <AccountPage />;
    case 'legacy': return <LegacyPresenter />;
    default:
      return <div className="ps-not-found"><Brand /><h1>Diese Seite gibt es nicht.</h1><ProductLink to="/">Zur Startseite</ProductLink></div>;
  }
}

export default function ProductShell() {
  const route = useProductRoute();
  return (
    <ProductAuthProvider>
      <ProductBillingProvider>
        <RouteOutlet route={route} />
      </ProductBillingProvider>
    </ProductAuthProvider>
  );
}
